import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import DescargasService from "../../../services/DownloadService";
import useStep7Generation from "../../../core/sesionGenerator/hooks/useStep7Generation";
import ProgressBar from "../../../core/sesionGenerator/components/ProgressBar";

import "./style_steps/step3.css";

const LS_KEY = "sae_step3_generado";

const Paso3 = ({ selectores, onBack }) => {
  const [filas, setFilas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [savedMap, setSavedMap] = useState({});

  // 🔧 Hook unificado (Step7)
  const {
    progress,
    isGenerating,
    mongoId,
    startGeneration,
    resetProgress,
    restoreProgress,
    getLatestProgress,
    setExistingMongoId
  } = useStep7Generation();

  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const showToast = (severity, message) =>
    setToast({ open: true, severity, message });

  const stepLabels = {
    mongo: "Generación de sesión",
    word: "Documento Word",
    pdf: "Documento PDF",
    html: "Vista HTML",
  };

  /* ===================================================
     SNAPSHOT / RESTORE (CLAVE)
     =================================================== */
  const buildSnapshot = (fila) => ({
    id_PSQL: fila.id_PSQL,
    sub_area_id: fila.sub_area_id,
    tema: fila.tema,
    objetivo: fila.objetivo,
  });

  const hasChanged = (fila, snapshot) => {
    if (!snapshot) return true;
    return JSON.stringify(buildSnapshot(fila)) !== JSON.stringify(snapshot);
  };

  /* ===================================================
     CARGA DESDE PASO 2
     =================================================== */
  useEffect(() => {
    const paso2 = JSON.parse(localStorage.getItem("seleccionPaso2"));
    if (!paso2?.temasPorSubarea) {
      setFilas([]);
      return;
    }

    const nuevasFilas = Object.entries(paso2.temasPorSubarea).flatMap(
      ([subAreaId, sub]) =>
        (sub.temas || []).map((temaObj) => ({
          id_PSQL: temaObj.id_PSQL,
          sub_area_id: Number(subAreaId),           // 👈 CLAVE
          sub_area_tematica: sub.nombre,
          tema: temaObj.tema,
          objetivo: temaObj.descripcion || "",
          mongoId: null,
        }))
    );

    setFilas(nuevasFilas);

    const saved = JSON.parse(localStorage.getItem(LS_KEY)) || {};
    setSavedMap(saved);
  }, []);

  /* ===================================================
     AGRUPAR POR SUBÁREA
     =================================================== */
  const gruposPorSubarea = useMemo(() => {
    const map = new Map();
    filas.forEach((f, idx) => {
      const key = f.sub_area_tematica || "Sin subárea";
      if (!map.has(key)) map.set(key, { subarea: key, items: [] });
      map.get(key).items.push({ ...f, __idx: idx });
    });
    return Array.from(map.values());
  }, [filas]);

  /* ===================================================
     GENERAR / VER / RESTORE
     =================================================== */
  const handleGenerar = async (index) => {
    const fila = filas[index];
    if (!fila?.id_PSQL) {
      showToast("warning", "Sesión sin id válido.");
      return;
    }

    const saved = savedMap[fila.id_PSQL];
    const changed = hasChanged(fila, saved?.snapshot);

    setFilaSeleccionada(fila);
    setModalOpen(true);

    if (saved && !changed) {
      resetProgress();
      restoreProgress(saved.progreso);
      setExistingMongoId(saved.mongoId); 
      return;
    }


    if (isGenerating) return;

    resetProgress();

    try {
      const sesion = {
        id_PSQL: String(fila.id_PSQL),
        tipo_sesion: "SAE",
        nivel: selectores.nivel_nombre,
        grado: Number(selectores.grado_nombre),
        area_tematica: selectores.area_nombre,
        tema: fila.tema,
        objetivo: fila.objetivo,
        sub_area_tematica: fila.sub_area_tematica,
      };

      const generatedMongoId = await startGeneration({ sesion });
      if (!generatedMongoId) return;

      const finalProgress = getLatestProgress();

      const updated = {
        ...savedMap,
        [fila.id_PSQL]: {
          mongoId: generatedMongoId,
          snapshot: buildSnapshot(fila),
          progreso: finalProgress,
        },
      };

      localStorage.setItem(LS_KEY, JSON.stringify(updated));
      setSavedMap(updated);

      showToast("success", "Sesión SAE generada correctamente.");
    } catch (e) {
      console.error(e);
      showToast("error", "No se pudo generar la sesión.");
    }
  };

  /* ===================================================
     UI
     =================================================== */
  return (
    <Box className="step3-root">
      <Box className="step3-header">
        <Typography variant="h5">Generación de Sesiones de Aprendizaje</Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {gruposPorSubarea.map((g, i) => (
          <Accordion key={g.subarea} defaultExpanded={i === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Subárea: {g.subarea}</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sesión</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {g.items.map((fila) => {
                      const saved = savedMap[fila.id_PSQL];
                      const changed = hasChanged(fila, saved?.snapshot);

                      return (
                        <TableRow key={fila.__idx}>
                          <TableCell>{fila.tema}</TableCell>
                          <TableCell>{fila.objetivo}</TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              disabled={isGenerating}
                              onClick={() => handleGenerar(fila.__idx)}
                            >
                              {saved && !changed ? "Ver" : "Generar"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Box className="step3-footer">
        <Button variant="outlined" onClick={onBack}>
          Atrás
        </Button>
      </Box>

      {/* MODAL */}
      <Modal
        open={modalOpen}
        disableScrollLock
        onClose={(event, reason) => {
          if (isGenerating) return;
          setModalOpen(false);
        }}
        disableEscapeKeyDown={isGenerating}
      >
        <Box
          className="step3-modal-box"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          {filaSeleccionada && (
            <>
              <Typography variant="h6" gutterBottom>
                {filaSeleccionada.tema}
              </Typography>
              {isGenerating && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  ⏱️ Este proceso puede tardar aproximadamente 2 minutos. Por favor, no cierres esta ventana.
                </Typography>
              )}

              {["mongo", "word", "pdf", "html"].map((s) => (
                <ProgressBar
                  key={s}
                  label={stepLabels[s]}
                  estado={progress[s]?.estado}
                  progreso={progress[s]?.progreso}
                  tiempo={progress[s]?.tiempo}
                />
              ))}

              <Box className="sae-modal-actions">
                {["word", "pdf", "html"].map((t) => (
                  <Button
                    key={t}
                    variant="contained"
                    disabled={progress[t]?.estado !== "completado"}
                    onClick={() =>
                      DescargasService.descargarArchivoSesion(
                        mongoId,
                        "SAE",
                        t,
                        filaSeleccionada,
                      )
                    }
                  >
                    Descargar {t.toUpperCase()}
                  </Button>
                ))}

                <Button
                  variant="outlined"
                  disabled={isGenerating}
                  onClick={() => setModalOpen(false)}
                >
                  Cerrar
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Paso3;
